import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { darken } from 'polished';
import moment from 'moment';
import BasicLayout from 'components/layout/basic-layout';
import colors from 'theme/colors';
import { EUpdateModalButton, EUpdateModalTitle, EUpdateModalSubTitle } from 'lib/storage';
import { isAuthorizedUserAdmin } from 'lib/auth';
import { ActionBar, Container } from 'pages/compare/components';
import ButtonSimple from 'components/buttons/simple';
import { toast } from 'react-toastify';
import messages from 'constants/messages';
import DeleteUpdateIcon from 'assets/svg/DeleteUpdateIcon';
import EditUpdateIcon from 'assets/svg/EditUpdateIcon';
import { IProductReleasesData } from 'store/add-new-update/types';
import AddNewUpdateModal from './add-new-update';
import { TAddNewUpdatePageProps } from '.';
import PostUpdateModal from './add-new-update/post-update';
import DeleteUpdateModal from './delete-update';

const { labelPlatinum } = colors;

const Head = styled.h2`
  font-size: 22px;
  font-weight: 500;
  margin-left: 72px;
`;

export const HeaderPanel = styled(ActionBar)`
  padding: 0;
`;

export const ContentPanel = styled.div`
  padding: 0;
`;

export const ContentContainer = styled.div`
  margin-top: 15px;
  margin-left: 72px;
  width: 66%;
`;

const DateContainer = styled.h2`
  font-size: 18px;
  font-weight: 500;
  font-color: ${colors.modalDateGray};
  opacity: 60%;
  display: flex;
  flex-direction: row;
  margin: 0;
  margin-right: 65%;
  width: 25%;
`;

const UpdateTitleContainer = styled.h3`
  font-size: 16px;
  font-weight: 600;
  font-color: #303641;
  margin: 15px 0px 8px 0px;
  width: 77%;
`;

const BorderContainer = styled.div`
  border: solid 1px #d1d1d6;
  width: 100%;
  margin: 12px 0px;
`;

const lightenValue = 0.2;

export const ButtonContainer = styled.div`
  display: flex;
  align-items: center;
  transition: color 0.2s ease, fill 0.2s ease;
  :hover {
    cursor: pointer;
    color: ${darken(lightenValue, labelPlatinum)};
  }
`;

const List = styled.ul`
  border: none;
  background-color: white;
  margin: 0;
  list-style-type: none;
  padding: 0px;
`;

const RowContainer = styled.div`
  display: flex;
  align-items: center;
`;

const DescrContainer = styled.div`
  width: 95%;
`;

const NoProductRelease = styled.span`
  font-weight: 500;
`;

export const NewUpdateButton = styled(ButtonSimple)`
  margin-right: 72px;
  min-width: 130px;
`;

export default ({
  setAddNewUpdateModalIsOpen,
  setPostUpdateModalIsOpen,
  setPostUpdateConfirmButtonPressed,
  setDeleteUpdateModalIsOpen,
  getproductReleases,
  productReleases,
  isDeleteProduct,
  isAddProduct,
  isProductReleasesFetched,
}: TAddNewUpdatePageProps) => {
  useEffect(() => {
    if (productReleases.length === 0 && !isProductReleasesFetched) {
      getproductReleases();
    }
  }, [productReleases, getproductReleases, isProductReleasesFetched]);

  useEffect(() => {
    if (isDeleteProduct) {
      toast.success(`${messages.success.productReleasesDeleted}`, { autoClose: 3000 });
    }
  }, [isDeleteProduct]);

  useEffect(() => {
    if (isAddProduct) {
      toast.success(`${messages.success.productReleasesUpdated}`, { autoClose: 3000 });
    }
  }, [isAddProduct]);

  const [isUserAdmin, setIsUserAdmin] = useState(false);

  useEffect(() => {
    setIsUserAdmin(isAuthorizedUserAdmin());
  }, []);

  return (
    <>
      <BasicLayout data-testid="whats-new-page">
        <Container>
          <HeaderPanel>
            <Head>Whats new</Head>
            {isUserAdmin && (
              <NewUpdateButton
                type="button"
                data-testid="add-new-update-button"
                onClick={() => {
                  setAddNewUpdateModalIsOpen(
                    true,
                    EUpdateModalTitle.NEW_UPDATE,
                    EUpdateModalButton.NEW_UPDATE,
                    EUpdateModalSubTitle.NEW_UPDATE
                  );
                  setPostUpdateModalIsOpen(false);
                  setPostUpdateConfirmButtonPressed(false);
                  setDeleteUpdateModalIsOpen(false);
                }}
              >
                New Update
              </NewUpdateButton>
            )}
          </HeaderPanel>
          <ContentPanel>
            <AddNewUpdateModal />
            <PostUpdateModal />
          </ContentPanel>
          <ContentContainer>
            {productReleases.length > 0 && (
              <List>
                {productReleases.map((proRel: IProductReleasesData) => (
                  <li key={proRel.id}>
                    <RowContainer>
                      <DateContainer>{moment(proRel.createdAt).format('MMMM DD, YYYY')}</DateContainer>
                      {isUserAdmin && (
                        <ButtonContainer>
                          <EditUpdateIcon
                            dataTestId="edit-update-icon"
                            onClick={() => {
                              setAddNewUpdateModalIsOpen(
                                true,
                                EUpdateModalTitle.EDIT_UPDATE,
                                EUpdateModalButton.EDIT_UPDATE,
                                EUpdateModalSubTitle.EDIT_UPDATE,
                                proRel
                              );
                            }}
                          />
                          <DeleteUpdateIcon
                            dataTestId="delete-update-icon"
                            onClick={() => {
                              setDeleteUpdateModalIsOpen(true, proRel.id);
                            }}
                          />
                        </ButtonContainer>
                      )}
                    </RowContainer>
                    <RowContainer>
                      <UpdateTitleContainer>{proRel.title}</UpdateTitleContainer>
                    </RowContainer>
                    <DescrContainer dangerouslySetInnerHTML={{ __html: proRel.description }} />
                    <BorderContainer />
                  </li>
                ))}
              </List>
            )}
            {isProductReleasesFetched && productReleases.length === 0 && (
              <div>
                <div>
                  No product updates created till now. <br /> Click on <NoProductRelease>“New Update”</NoProductRelease>{' '}
                  to create one.
                </div>
              </div>
            )}
          </ContentContainer>
        </Container>
      </BasicLayout>
      <DeleteUpdateModal />
    </>
  );
};
